import React from 'react';
import { App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const OvertimeFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();

  const save = async () => {
    try {
      const v = await form.validateFields();
      const created = await hrmsService.createOtRequest({
        ...v,
        ot_date: v.ot_date.format('YYYY-MM-DD'),
      });
      message.success('OT request created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.otRequests() });
      navigate(`/hr/overtime/${created?.id || ''}`);
    } catch {
      message.error('Create failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="New OT request"
          subtitle="Request overtime hours"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/overtime')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical" initialValues={{ excess_disposition: 'TOIL', ot_kind: 'Normal' }}>
        <Card title="OT details">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="ot_date" label="OT date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="requested_hours" label="Hours" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.25} step={0.25} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="ot_kind"
                label="OT kind"
                rules={[{ required: true }]}
                help="Selects which salary head / rate payroll uses (Normal, Friday, Holiday, Night)"
              >
                <Select
                  options={[
                    { value: 'Normal', label: 'Normal day OT' },
                    { value: 'Friday', label: 'Friday / rest-day OT' },
                    { value: 'Holiday', label: 'Public holiday OT' },
                    { value: 'Night', label: 'Night differential OT' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="excess_disposition" label="Excess disposition">
                <Select
                  options={[
                    { value: 'Pay', label: 'Pay excess in payroll' },
                    { value: 'TOIL', label: 'Bank excess as TOIL' },
                    { value: 'Discard', label: 'Discard excess' },
                    { value: 'Defer', label: 'Defer excess' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default OvertimeFormPage;
